import React from 'react';
import PropTypes from 'prop-types';

import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import { qformat, DATE_FORMATS } from 'views_shared/app/javascript/utils/dateFormatters';
import { profileUrl } from 'views_shared/app/javascript/utils/urls';

import Attachment from '../Attachment';
import { containsVideoRequest } from '../videoRequest';
import MessageContent from './MessageContent';
import VideoRequestCard from './VideoRequestCard';

function Message({ message, conversation }) {
  // System rows (e.g. "Alyssa added Bob to the conversation") — no avatar, no profile link, no attachments.
  if (message.kind && message.kind !== 'user') {
    return (
      <div className="my-3 text-center">
        <span className="text-secondary paragraph-sm">{message.body}</span>
      </div>
    );
  }

  // Match the sender by stable profile id (slugs are mutable friendly-ids that go stale on a
  // rename — a renamed sender would otherwise render blank). Fall back to slug for optimistic
  // sends and any row lacking an id. Check both arrays: a receiver who replies appears in the
  // senders array only after the materialized view refreshes.
  const matchesSender = (p) => (message.sender_profile_id != null && p.id === message.sender_profile_id)
    || p.slug === message.sender_profile_slug;
  const messageSenderProfile = conversation.conversation_senders.find(matchesSender)
    || conversation.conversation_receivers.find(matchesSender);

  if (!messageSenderProfile) return null;

  // "Request a video" CTA — recipient view only: the requester sees their own message plain.
  const isOwnMessage = (message.sender_profile_id != null
      && String(message.sender_profile_id) === String(globalThis.Qwoted.CURRENT_PROFILE_ID))
    || message.sender_profile_slug === globalThis.Qwoted.CURRENT_SLUG;
  // metadata.video_request = send-time intent stamp (edit-proof); text heuristic covers
  // pre-stamp historical messages until it can be retired.
  const showVideoRequestCta = !isOwnMessage
    && (!!message.metadata?.video_request || containsVideoRequest(message.body));
  const senderFirstName = messageSenderProfile.name?.split(' ')[0] || messageSenderProfile.name;

  return (
    <div className="mb-3 p-2">
      <div className="d-inline-flex align-items-center mb-2 ms-3">
        <a
          href={profileUrl(messageSenderProfile)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View ${messageSenderProfile.name}'s profile`}
        >
          <ImageCropped
            roundedCircle
            src={messageSenderProfile.profile_image_url}
            alt={messageSenderProfile.name}
            size={40}
            wrapperClasses="me-3"
            fallbackToInitials
          />
        </a>
        <a
          className="fw-bold me-2 message-sender-name"
          href={profileUrl(messageSenderProfile)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {messageSenderProfile.name}
        </a>
        <div className="small text-secondary lh-lg">{qformat(message.sent_at, DATE_FORMATS.time)}</div>
      </div>

      <div className="message-body">
        <MessageContent body={message.body} contentFormat={message.content_format} />
        {message.message_attachments && message.message_attachments.map((attachment) => (
          <Attachment key={attachment.id} attachment={attachment} />
        ))}
        {showVideoRequestCta && <VideoRequestCard senderFirstName={senderFirstName} />}
      </div>
    </div>
  );
}

const profileShape = PropTypes.shape({
  slug: PropTypes.string,
  name: PropTypes.string,
});

Message.propTypes = {
  message: PropTypes.shape({
    message_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    sender_profile_slug: PropTypes.string.isRequired,
    sent_at: PropTypes.string.isRequired,
    body: PropTypes.string,
    content_format: PropTypes.string,
    kind: PropTypes.string,
    message_attachments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      }),
    ),
  }).isRequired,
  conversation: PropTypes.shape({
    conversation_senders: PropTypes.arrayOf(profileShape).isRequired,
    conversation_receivers: PropTypes.arrayOf(profileShape).isRequired,
  }).isRequired,
};

export default Message;
